import { ValueDepreciationCalculator } from './value-deprication.calculator';
import type { ICarInformation } from '../models/car-information.model';
import type { ITravelInformation } from '../components/TravelInformation';
import type { ITotalCostResult } from '../models/result.model';
import { CarUser } from '../models/car-user.enum';
import { FuelCalculator } from './fuel.calculator';
import { ValueDepreciationScenario } from '../models/value-depreciation-scenario.enum';

export class TotalCarCostsCalculator {
  static calculateMonthlyCost(
    monthlyFuelCost: number | undefined,
    congestionTaxSekPerMonth: number | undefined,
    parkingFeeSekPerMonth: number | undefined,
    vehicleTaxPerMonth: number | undefined,
    insuranceCostPerMonth: number | undefined,
    travelDeductionSekPerMonth: number | undefined
  ): number {
    return (monthlyFuelCost || 0) + (congestionTaxSekPerMonth || 0) + (parkingFeeSekPerMonth || 0) + (vehicleTaxPerMonth || 0) + (insuranceCostPerMonth || 0) - (travelDeductionSekPerMonth || 0);
  }

  static calculateTotalCost(carUser: CarUser, serviceCost: number, loanCost: number, fixedDepreciationCostPerMonth: number, monthlyCost: number, leasingCostPerMonth: number): number {
    return carUser === CarUser.CarOwner ? serviceCost + loanCost + fixedDepreciationCostPerMonth + monthlyCost : leasingCostPerMonth + monthlyCost;
  }

  static calculateDrivingDistance(travelInformation: ITravelInformation, carInformation: ICarInformation) {
    if (!(travelInformation.distanceFromHomeToWorkKmState && travelInformation.travelDaysPerMonth && carInformation.drivingDistancePerYearKm)) {
      return { yearlyCommuteGreatherThanTotalDistance: false, yearlyCommuteKm: -1 };
    }

    const yearlyCommuteKm = travelInformation.distanceFromHomeToWorkKmState * 2 * travelInformation.travelDaysPerMonth * 12;
    const yearlyCommuteGreatherThanTotalDistance = yearlyCommuteKm > carInformation.drivingDistancePerYearKm;
    return { yearlyCommuteGreatherThanTotalDistance, yearlyCommuteKm };
  }

  static calculateCost(carInformation: ICarInformation, travelInformation: ITravelInformation): ITotalCostResult {
    if (!carInformation.newCarPriceSek || !travelInformation.travelDaysPerMonth || !carInformation.fuelType || !travelInformation.distanceFromHomeToWorkKmState) {
      return {
        totalCost: 0,
        depreciation: 0,
        totalFuelCost: 0,
        insuranceCost: 0,
        loanCost: 0,
        leasingCostPerMonth: 0,
        parkingFee: 0,
        vehicleTax: 0,
        congestionTax: 0,
        serviceCost: 0,
        travelDeduction: 0,
        yearlyCommuteGreatherThanTotalDistance: false,
        yearlyCommuteKm: 0,
      };
    }

    const fixedDepreciationCostPerMonth = ValueDepreciationCalculator.calculateVariableDepreciationCostPerMonth(
      {
        newCarPriceSek: carInformation.newCarPriceSek,
        carAgeInYears: carInformation.modelYear ? new Date().getFullYear() - parseInt(carInformation.modelYear, 10) : 0,
        valueDepreciationScenario: carInformation.estimatedCarValueDeprication || ValueDepreciationScenario.Normal,
      },
      undefined
    );

    const { yearlyCommuteGreatherThanTotalDistance, yearlyCommuteKm } = this.calculateDrivingDistance(travelInformation, carInformation);
    const monthlyFuelCost = FuelCalculator.calculateMonthlyFuelCost({
      consumptionUnitsPer10Km: carInformation.fuelConsumptionUnitPerKm || 0,
      fuelPriceSek: carInformation.fuelPriceSekPerUnit || 0,
      fuelType: carInformation.fuelType,
      travelDaysPerMonth: travelInformation.travelDaysPerMonth,
      distanceToWorkKm: travelInformation.distanceFromHomeToWorkKmState,
      // NV-6014: If the distance to work and home per year exceeds the driving distance per year, use distance to work and home per year
      drivingDistancePerYearKm: yearlyCommuteGreatherThanTotalDistance ? yearlyCommuteKm : carInformation.drivingDistancePerYearKm,
      workDrivingDistancePerMonthKm: travelInformation.distanceFromHomeToWorkKmState * travelInformation.travelDaysPerMonth * 2,
    });

    const depreciation = fixedDepreciationCostPerMonth || 0;
    const totalFuelCost = Math.round(monthlyFuelCost) || 0;
    const insuranceCost = carInformation.insuranceCostPerYear ? Math.round(carInformation.insuranceCostPerYear / 12) : 0;
    const loanCost = carInformation.carLoanPerMonth ? Math.round(carInformation.carLoanPerMonth) : 0;
    const leasingCostPerMonth = carInformation.leasingCostPerMonth ? Math.round(carInformation.leasingCostPerMonth) : 0;
    const parkingFee = travelInformation.parkingFeeSekPerDay ? Math.round(travelInformation.parkingFeeSekPerDay * travelInformation.travelDaysPerMonth) : 0;
    const vehicleTax = carInformation.vehicleTaxPerYear ? Math.round(carInformation.vehicleTaxPerYear / 12) : 0;
    const congestionTax = travelInformation.congestionTaxSekPerDay ? Math.round(travelInformation.congestionTaxSekPerDay * travelInformation.travelDaysPerMonth) : 0;
    const serviceCost = carInformation.serviceAndRepairCostPerYear ? Math.round(carInformation.serviceAndRepairCostPerYear / 12) : 0;
    const travelDeduction = travelInformation.travelDeductionSekPerYear ? Math.round(travelInformation.travelDeductionSekPerYear / 12) : 0;

    const monthlyCost = this.calculateMonthlyCost(monthlyFuelCost || 0, congestionTax || 0, parkingFee || 0, vehicleTax, insuranceCost, travelDeduction);

    const totalCost = this.calculateTotalCost(carInformation.carUser, serviceCost, loanCost, fixedDepreciationCostPerMonth, monthlyCost, leasingCostPerMonth);

    return {
      totalCost: totalCost > 0 ? totalCost : 0,
      depreciation,
      totalFuelCost,
      insuranceCost,
      loanCost,
      leasingCostPerMonth,
      parkingFee,
      vehicleTax,
      congestionTax,
      serviceCost,
      travelDeduction,
      yearlyCommuteGreatherThanTotalDistance,
      yearlyCommuteKm,
    };
  }
}
